import { Api } from '../services/api.ts';
import type { AtlasArchivoVisual, AtlasCaso } from '../types/index.ts';
import { compressImageToBase64 } from '../utils/imageCompressor.ts';

let archivosCache: AtlasArchivoVisual[] = [];
let casosCache: AtlasCaso[] = [];
let activeFilter = 'TODOS';

function showToast(msg: string) {
  const toast = document.getElementById('save-status-toast');
  if (toast) {
    toast.innerText = msg;
    toast.classList.add('show');
    setTimeout(() => toast.classList.remove('show'), 2800);
  }
}

export function initImagenes() {
  const gridContainer = document.getElementById('visual-archive-grid') as HTMLElement | null;
  const filterContainer = document.getElementById('visual-filter-buttons') as HTMLElement | null;
  const formAddVisual = document.getElementById('form-add-visual') as HTMLFormElement | null;
  const inputEditId = document.getElementById('visual-id-edit') as HTMLInputElement | null;
  const formTitle = document.getElementById('visual-form-title') as HTMLElement | null;
  const btnSubmitText = document.getElementById('btn-submit-visual-text') as HTMLElement | null;
  const btnCancelEdit = document.getElementById('btn-cancel-visual-edit') as HTMLButtonElement | null;
  const selectCaso = document.getElementById('visual-caso-id') as HTMLSelectElement | null;
  const inputImageFile = document.getElementById('visual-file-input') as HTMLInputElement | null;
  const imagePreviewBox = document.getElementById('visual-file-preview') as HTMLElement | null;

  let currentImageBase64 = '';

  async function loadData() {
    try {
      const [casos, archivos] = await Promise.all([
        Api.getCasos(),
        Api.getArchivosVisuales()
      ]);
      casosCache = casos;
      archivosCache = archivos;

      populateCasosSelect();
      render();
    } catch (err) {
      console.error('Error al cargar archivo visual desde SQLite:', err);
    }
  }

  function populateCasosSelect() {
    if (!selectCaso) return;
    const currentVal = selectCaso.value;
    selectCaso.innerHTML = '';
    casosCache.forEach(c => {
      const opt = document.createElement('option');
      opt.value = c.id.toString();
      opt.textContent = `${c.name} (${c.codigo})`;
      selectCaso.appendChild(opt);
    });
    if (currentVal) selectCaso.value = currentVal;
  }

  function render() {
    if (!gridContainer) return;
    gridContainer.innerHTML = '';

    const filtered = archivosCache.filter(a => {
      if (activeFilter === 'TODOS') return true;
      return a.caso_nombre?.includes(activeFilter) || a.tipo_documento === activeFilter;
    });

    if (filtered.length === 0) {
      gridContainer.innerHTML = `
        <div style="grid-column: 1 / -1; padding: 40px; text-align: center; color: var(--text-muted); background: #ffffff; border: 1.5px dashed var(--border); border-radius: 8px;">
          <p style="font-size:0.92rem; font-weight:600; margin-bottom:4px;">No hay láminas registradas para este filtro</p>
          <span style="font-size:0.8rem; color:var(--text-muted);">Registra una nueva lámina, plano o croquis en el formulario a continuación.</span>
        </div>
      `;
      return;
    }

    filtered.forEach(a => {
      const card = document.createElement('div');
      card.className = 'visual-card';
      card.style.cssText = `
        background: #ffffff;
        border: 1.5px solid var(--border);
        border-radius: 8px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
      `;

      card.innerHTML = `
        <div class="visual-card-thumb" style="height:175px; background:#f8fafc; position:relative; overflow:hidden; display:flex; align-items:center; justify-content:center; border-bottom:1px solid var(--border);">
          ${a.imagen 
            ? `<img src="${a.imagen}" alt="${escapeHtml(a.name)}" style="width:100%; height:100%; object-fit:cover; transition:transform 0.2s;" onmouseover="this.style.transform='scale(1.03)'" onmouseout="this.style.transform='scale(1)'">`
            : `<div class="visual-card-placeholder" style="display:flex; flex-direction:column; align-items:center; gap:8px; color:var(--text-muted);">
                 <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                 <span style="font-size:0.75rem; font-weight:600;">${formatTipoDoc(a.tipo_documento)}</span>
               </div>`
          }
          <span class="visual-tag-badge" style="position:absolute; top:8px; left:8px; background:rgba(255,255,255,0.92); color:var(--text-sidebar-title); font-size:0.7rem; font-weight:700; padding:2px 8px; border-radius:4px; box-shadow:0 1px 3px rgba(0,0,0,0.15);">
            ${escapeHtml(a.caso_nombre || 'Caso de Estudio')}
          </span>
          ${a.url_origen ? `<a href="${escapeHtml(a.url_origen)}" target="_blank" rel="noopener" style="position:absolute; top:8px; right:8px; background:rgba(255,255,255,0.92); color:var(--strawbs); font-size:0.7rem; font-weight:700; padding:2px 7px; border-radius:4px; text-decoration:none;" title="Ver fuente digital original">↗ Fuente</a>` : ''}
        </div>
        <div class="visual-card-body" style="padding:14px; display:flex; flex-direction:column; flex:1;">
          <h4 style="font-size:0.9rem; font-weight:700; color:var(--text-main); line-height:1.35; margin-bottom:8px;">${escapeHtml(a.name)}</h4>
          <p class="visual-card-meta" style="font-size:0.76rem; color:var(--text-muted); display:flex; flex-direction:column; gap:3px; margin-bottom:12px;">
            <span><strong>Periodo:</strong> ${escapeHtml(a.fecha_periodo || '2026')}</span>
            <span><strong>Autor/Fuente:</strong> ${escapeHtml(a.autor_fuente || 'Aurora Damke Díaz')}</span>
          </p>
          <div class="visual-card-footer" style="margin-top:auto; display:flex; justify-content:space-between; align-items:center; padding-top:10px; border-top:1px dashed var(--border);">
            <span class="badge" style="background:var(--primary-subtle); color:var(--strawbs); font-size:0.72rem; font-weight:700;">${formatTipoDoc(a.tipo_documento)}</span>
            <div style="display:flex; gap:6px; align-items:center;">
              <button data-edit-visual="${a.id}" class="btn-secondary" style="padding:4px 9px; font-size:0.74rem;" title="Editar esta lámina en SQLite">
                Editar
              </button>
              <button data-del-visual="${a.id}" class="btn-del" style="padding:4px 7px;" title="Eliminar lámina de SQLite">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              </button>
            </div>
          </div>
        </div>
      `;
      gridContainer.appendChild(card);
    });

    // Eventos Editar
    gridContainer.querySelectorAll('button[data-edit-visual]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-edit-visual');
        const item = archivosCache.find(x => x.id.toString() === id);
        if (!item) return;
        editVisual(item);
      });
    });

    // Eventos Eliminar
    gridContainer.querySelectorAll('button[data-del-visual]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = btn.getAttribute('data-del-visual');
        if (!id) return;
        if (confirm('¿Está segura de que desea eliminar esta lámina o plano de SQLite?')) {
          try {
            await Api.deleteArchivoVisual(id);
            archivosCache = archivosCache.filter(a => a.id.toString() !== id);
            render();
            showToast('Lámina eliminada de SQLite');
          } catch (e) {
            alert('Error al eliminar elemento visual de SQLite');
          }
        }
      });
    });
  }

  function editVisual(item: AtlasArchivoVisual) {
    if (inputEditId) inputEditId.value = item.id.toString();
    if (selectCaso) selectCaso.value = item.caso_id.toString();

    const inName = document.getElementById('visual-name') as HTMLInputElement | null;
    const inTipo = document.getElementById('visual-tipo') as HTMLSelectElement | null;
    const inPeriodo = document.getElementById('visual-periodo') as HTMLInputElement | null;
    const inFuente = document.getElementById('visual-fuente') as HTMLInputElement | null;
    const inUrl = document.getElementById('visual-url') as HTMLInputElement | null;

    if (inName) inName.value = item.name || '';
    if (inTipo) inTipo.value = item.tipo_documento || 'plano_hist';
    if (inPeriodo) inPeriodo.value = item.fecha_periodo || '';
    if (inFuente) inFuente.value = item.autor_fuente || '';
    if (inUrl) inUrl.value = item.url_origen || '';

    currentImageBase64 = item.imagen || '';

    if (imagePreviewBox) {
      if (currentImageBase64) {
        imagePreviewBox.innerHTML = `
          <div style="display:flex; align-items:center; gap:12px; background:#f8fafc; border:1px solid var(--border); padding:8px 12px; border-radius:6px;">
            <img src="${currentImageBase64}" style="max-height:80px; max-width:120px; border-radius:4px; object-fit:contain; border:1px solid var(--border);">
            <div>
              <span style="font-size:0.75rem; color:#16a34a; font-weight:700; display:block;">✓ Imagen almacenada en Base64</span>
              <span style="font-size:0.72rem; color:var(--text-muted);">Selecciona un nuevo archivo si deseas reemplazarla.</span>
            </div>
          </div>
        `;
      } else {
        imagePreviewBox.innerHTML = '';
      }
    }

    if (formTitle) formTitle.innerText = `Editando Lámina: ${item.name}`;
    if (btnSubmitText) btnSubmitText.innerText = 'Actualizar Lámina en SQLite';
    if (btnCancelEdit) btnCancelEdit.style.display = 'inline-flex';

    formTitle?.scrollIntoView({ behavior: 'smooth' });
  }

  function resetForm() {
    if (formAddVisual) formAddVisual.reset();
    if (inputEditId) inputEditId.value = '';
    currentImageBase64 = '';
    if (imagePreviewBox) imagePreviewBox.innerHTML = '';
    if (formTitle) formTitle.innerText = 'Registrar Nueva Lámina o Plano en SQLite';
    if (btnSubmitText) btnSubmitText.innerText = 'Guardar Lámina en SQLite';
    if (btnCancelEdit) btnCancelEdit.style.display = 'none';
  }

  btnCancelEdit?.addEventListener('click', () => {
    resetForm();
  });

  function formatTipoDoc(tipo: string): string {
    const map: Record<string, string> = {
      'plano_hist': 'Plano Histórico',
      'cartografia': 'Cartografía SIG',
      'foto_campo': 'Foto de Campo',
      'croquis': 'Croquis / Bow-Wow',
      'archivo': 'Documento Archivo',
      'diagrama': 'Sección Transecto',
      'otro': 'Material Gráfico'
    };
    return map[tipo] || tipo;
  }

  // Filtros territoriales
  filterContainer?.querySelectorAll('button[data-filter]').forEach(btn => {
    btn.addEventListener('click', () => {
      filterContainer.querySelectorAll('button[data-filter]').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      activeFilter = btn.getAttribute('data-filter') || 'TODOS';
      render();
    });
  });

  // Manejo de carga de imagen con compresión inteligente
  inputImageFile?.addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;

    if (imagePreviewBox) {
      imagePreviewBox.innerHTML = `
        <div style="font-size:0.76rem; color:var(--strawbs); display:flex; align-items:center; gap:6px;">
          <span class="status-indicator-dot"></span>
          <span>Comprimiendo imagen con preservación de fidelidad arquitectónica...</span>
        </div>
      `;
    }

    try {
      // Compresión automática a Base64 WebP/JPEG optimizado (max 1600x1600, calidad 0.82)
      const res = await compressImageToBase64(file, 1600, 1600, 0.82);
      currentImageBase64 = res.dataUrl;

      const origKb = (res.originalSize / 1024).toFixed(0);
      const compKb = (res.compressedSize / 1024).toFixed(0);

      if (imagePreviewBox) {
        imagePreviewBox.innerHTML = `
          <div style="display:flex; align-items:center; gap:14px; background:#f0fdf4; border:1px solid #bbf7d0; padding:10px 14px; border-radius:6px; margin-top:6px;">
            <img src="${currentImageBase64}" style="max-height:85px; max-width:130px; border-radius:4px; object-fit:contain; border:1px solid #86efac; background:#fff;">
            <div>
              <span style="font-size:0.78rem; font-weight:700; color:#15803d; display:block;">
                ⚡ Imagen comprimida con éxito (${res.width}×${res.height}px)
              </span>
              <span style="font-size:0.74rem; color:#166534; margin-top:2px; display:block;">
                Original: <b>${origKb} KB</b> → Base64 optimizado: <b>${compKb} KB</b> 
                <span style="background:#dcfce7; padding:1px 6px; border-radius:3px; margin-left:4px; font-weight:700;">-${res.ratioPercent}%</span>
              </span>
            </div>
          </div>
        `;
      }
    } catch (err) {
      console.error('Error al comprimir imagen:', err);
      if (imagePreviewBox) {
        imagePreviewBox.innerHTML = '<span style="color:#dc2626; font-size:0.75rem;">Error al procesar archivo de imagen.</span>';
      }
    }
  });

  // Formulario de agregar / editar
  formAddVisual?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const editId = inputEditId?.value || '';
    const inCaso = document.getElementById('visual-caso-id') as HTMLSelectElement | null;
    const inName = document.getElementById('visual-name') as HTMLInputElement | null;
    const inTipo = document.getElementById('visual-tipo') as HTMLSelectElement | null;
    const inPeriodo = document.getElementById('visual-periodo') as HTMLInputElement | null;
    const inFuente = document.getElementById('visual-fuente') as HTMLInputElement | null;
    const inUrl = document.getElementById('visual-url') as HTMLInputElement | null;

    if (!inName || !inCaso || !inName.value.trim()) {
      alert('Por favor ingrese el título de la lámina o plano.');
      return;
    }

    const payload: Partial<AtlasArchivoVisual> = {
      caso_id: parseInt(inCaso.value) || 1,
      name: inName.value.trim(),
      tipo_documento: (inTipo?.value || 'plano_hist') as any,
      fecha_periodo: inPeriodo?.value.trim() || '2026',
      autor_fuente: inFuente?.value.trim() || 'Aurora Damke Díaz',
      url_origen: inUrl?.value.trim() || '',
      imagen: currentImageBase64
    };

    try {
      if (editId) {
        await Api.updateArchivoVisual(editId, payload);
        showToast('Lámina actualizada con éxito en SQLite');
      } else {
        await Api.addArchivoVisual(payload);
        showToast('Nueva lámina guardada en SQLite');
      }

      resetForm();
      await loadData();
    } catch (err: any) {
      console.error('Error al guardar documento visual:', err);
      alert('Error al guardar documento visual en SQLite: ' + (err?.message || err));
    }
  });

  loadData();
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.innerText = str || '';
  return div.innerHTML;
}
